import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import { Toaster } from 'sonner';
import LandingPage from './pages/LandingPage';
import RegisterPage from './pages/RegisterPage';
import LoginPage from './pages/LoginPage';
import SetupAdmin from './pages/SetupAdmin';
import UpgradeToMainAdmin from './pages/UpgradeToMainAdmin';
import WriterDashboard from './pages/WriterDashboard';
import ProofreaderDashboard from './pages/ProofreaderDashboard';
import TranslatorDashboard from './pages/TranslatorDashboard';
import AdminDashboard from './pages/AdminDashboard';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import PWAInstallPrompt from './components/PWAInstallPrompt';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Global auto-logout: if any request returns 401 (expired token, or a token that was
  // invalidated because the user's role changed), clear the session and send them to login.
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401 && localStorage.getItem('token')) {
          const detail = error.response.data?.detail || '';
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          setToken(null);
          setUser(null);
          if (typeof detail === 'string' && detail.includes('rol is verander')) {
            try { sessionStorage.setItem('logout_reason', detail); } catch (e) {}
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      // Only logout on 401/403 (auth errors), not on server errors (500/503)
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        logout();
      } else {
        console.error('Temporary server error, retrying...', error);
        // Retry once after 2 seconds for transient errors
        setTimeout(async () => {
          try {
            const retryResponse = await axios.get(`${API}/auth/me`);
            setUser(retryResponse.data);
          } catch (retryError) {
            console.error('Retry failed:', retryError);
            if (retryError.response && (retryError.response.status === 401 || retryError.response.status === 403)) {
              logout();
            }
          }
        }, 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <PWAInstallPrompt />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/setup-admin" element={<SetupAdmin />} />
          <Route path="/upgrade-to-main-admin" element={<UpgradeToMainAdmin />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/writer/*" element={<ProtectedRoute role="writer"><WriterDashboard /></ProtectedRoute>} />
          <Route path="/proofreader/*" element={<ProtectedRoute role="proofreader"><ProofreaderDashboard /></ProtectedRoute>} />
          <Route path="/translator/*" element={<ProtectedRoute role="translator"><TranslatorDashboard /></ProtectedRoute>} />
          <Route path="/admin/*" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

const ProtectedRoute = ({ children, role }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100">
        <div className="animate-spin h-12 w-12 border-4 border-orange-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (role && user.role !== role) {
    return <Navigate to="/" />;
  }

  return children;
};

export default App;
export { useAuth, API, BACKEND_URL };